import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { useScroll, useMotionValueEvent } from 'framer-motion';
import { LogOut, Settings, Menu, X, Moon, Sun } from 'lucide-react';

interface HeaderProps {
  isAuthenticated: boolean;
  linkedIn: string;
  github: string;
  mail: string;
  phone: string;
  onLogout: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

const iconBtn = 'w-11 h-11 rounded-full flex items-center justify-center text-zinc-400 hover:text-ink transition-colors';

export default function Header({ isAuthenticated, onLogout, theme, onToggleTheme }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (y) => setScrolled(y > 20));

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isFr = i18n.language.startsWith('fr');
  const isActive = (path: string) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  const navLinks = [
    { to: '/projects',   label: t('projects') },
    { to: '/formations', label: t('career') },
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors ${scrolled || mobileOpen ? 'bg-base/95 border-line' : 'bg-base border-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" aria-label={t('home') as string}>
              <span
                aria-hidden="true"
                className="block w-8 h-8 bg-ink"
                style={{ WebkitMask: 'url(/nav-icon.png) center / contain no-repeat', mask: 'url(/nav-icon.png) center / contain no-repeat' }}
              />
            </Link>

            <div className="flex items-center gap-1">
              <nav className="hidden md:flex items-center gap-1 mr-2">
                {navLinks.map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`px-4 h-11 inline-flex items-center text-sm font-medium rounded-full transition-colors ${isActive(to) ? 'text-ink bg-surface' : 'text-zinc-400 hover:text-ink'}`}
                  >
                    {label}
                  </Link>
                ))}
                <Link
                  to="/cv"
                  className={`px-5 h-11 inline-flex items-center text-sm font-semibold rounded-full transition-colors ${isActive('/cv') ? 'bg-primary text-white' : 'bg-ink text-base hover:bg-primary hover:text-white'}`}
                >
                  {t('cv_label')}
                </Link>
              </nav>

              <button
                onClick={() => { const next = isFr ? 'en' : 'fr'; localStorage.setItem('lang', next); i18n.changeLanguage(next); }}
                className={iconBtn}
                aria-label={isFr ? 'Switch to English' : 'Passer en français'}
              >
                <ReactCountryFlag countryCode={isFr ? 'FR' : 'GB'} svg style={{ width: '18px', height: '18px', borderRadius: '3px' }} />
              </button>

              <button onClick={onToggleTheme} className={iconBtn} aria-label={t(theme === 'dark' ? 'light_mode' : 'dark_mode') as string}>
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {isAuthenticated && (
                <>
                  <Link to="/admin" className={iconBtn} aria-label="Admin"><Settings size={18} /></Link>
                  <button onClick={onLogout} className={iconBtn} aria-label="Logout"><LogOut size={18} /></button>
                </>
              )}

              <button onClick={() => setMobileOpen(o => !o)} className={`md:hidden ${iconBtn}`} aria-label="Menu" aria-expanded={mobileOpen}>
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 pt-16 bg-base">
          <nav className="flex flex-col p-6 gap-1">
            {[{ to: '/', label: t('home') }, ...navLinks, { to: '/cv', label: t('cv_label') }].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`py-3 px-4 rounded-2xl font-display text-2xl font-bold transition-colors ${isActive(to) ? 'text-ink bg-surface' : 'text-zinc-300 hover:text-ink'}`}
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </>
  );
}
